import { useEffect, useState } from "react";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating, StarInput } from "@/components/StarRating";

export const ReviewsDetail = ({ onRequestLogin }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [rating, setRating] = useState(5);
  const [name, setName] = useState(user?.name || "");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => setReviews(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/reviews", { name, rating, comment });
      setSubmitted(true);
      setComment("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reviews-detail-content">
      <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-gradient-chrome flex items-center gap-2.5">
        <span className="text-xl">⭐</span> Customer Reviews
      </h3>
      <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-cyan-400/80">
        Real voices from the Autonomous Dex community
      </p>

      {/* Leave a review */}
      <div className="mt-6 rounded-xl border border-white/10 bg-black/40 p-5">
        <p className="font-heading font-bold text-white text-base flex items-center gap-2">
          <MessageSquarePlus className="w-4 h-4 text-cyan-400" /> Leave a Review
        </p>
        {user ? (
          submitted ? (
            <p data-testid="review-submitted-msg" className="mt-3 text-sm text-cyan-300">
              Thanks! Your review was submitted and is awaiting approval.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <StarInput value={rating} onChange={setRating} />
              <input
                data-testid="review-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none"
              />
              <textarea
                data-testid="review-comment-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience…"
                required
                rows={3}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none resize-none"
              />
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                data-testid="review-submit-button"
                className="btn-electric text-black font-bold text-sm px-6 py-2.5 rounded-lg disabled:opacity-60"
              >
                {busy ? "Submitting…" : "Submit Review"}
              </button>
            </form>
          )
        ) : (
          <div className="mt-3">
            <p className="text-sm text-slate-400">Log in or create an account to leave a review.</p>
            <button
              data-testid="reviews-login-prompt"
              onClick={onRequestLogin}
              className="btn-ghost-electric mt-3 border border-white/20 text-white font-semibold text-sm px-5 py-2.5 rounded-lg"
            >
              Log in / Sign up
            </button>
          </div>
        )}
      </div>

      {/* Existing reviews */}
      <div className="mt-8 space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <p className="text-sm text-slate-500">No reviews yet. Be the first!</p>
        ) : (
          reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`review-item-${r.id}`}
              className="rounded-xl border border-white/10 bg-black/40 p-5"
            >
              <div className="flex items-center justify-between">
                <p className="font-heading font-bold text-white text-sm">{r.name}</p>
                <StarRating value={r.rating} />
              </div>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">{r.comment}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
